import type { components } from "@/api/schema";

type AuditEvent = components["schemas"]["AuditEvent"];

/** Half-width of the date range built around an event's own timestamp. */
const CONTEXT_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface AuditLogNavigation {
  to: "/audit-log";
  search: {
    preset: "custom";
    date_from: string;
    date_to: string;
    offset: number;
    module?: "inventory";
    search?: string;
  };
}

/**
 * Builds a deep link from an Inventory stock-audit row to the global
 * /audit-log page (Tasks 1-3).
 *
 * ponytail: no backend field links a legacy stock-audit row to its global
 * `audit_events` id today (verified against atlas-oms-backend — no
 * `audit_event_id` anywhere), so this only builds the target-search
 * fallback below. Add an exact-event branch back if/when the backend
 * exposes one.
 *
 * The fallback searches by SKU + the "inventory" module and bounds the date
 * range to a window around the event's own timestamp — never an unbounded
 * "all time" query. Marketplace isn't a separate global-ledger filter (the
 * canonical event's target is SKU-only; marketplace only lives in its
 * metadata), so it isn't part of the fallback query.
 */
export function auditLogNavigationFor(
  sku: string,
  event: Pick<AuditEvent, "at" | "context">,
): AuditLogNavigation {
  const at = event.at ? new Date(event.at) : new Date();
  const anchor = Number.isNaN(at.getTime()) ? Date.now() : at.getTime();
  const date_from = new Date(anchor - CONTEXT_WINDOW_MS).toISOString();
  const date_to = new Date(anchor + CONTEXT_WINDOW_MS).toISOString();

  return {
    to: "/audit-log",
    search: {
      preset: "custom",
      date_from,
      date_to,
      offset: 0,
      ...(sku && { module: "inventory", search: sku }),
    },
  };
}
